import * as PIXI from "pixi.js"
import PixiScene from "../PixiScene";
import PixiSelector from "./PixiSelector";
import PixiCard from "./PixiCard";

import MessageHelper from "../../../../../helpers/MessageHelper";
import EventApi from "../../../../../api/EventsApi";

export default class PixiMessenger {

    public static scene: PixiScene;

    /**
     * Выделенные карты / пачки
     */
    public static getSelectedIds(card: PixiCard, fetchCards = false): Array<number>
    {
        let id = (card.zone === 'hand' || fetchCards) ? card.cardId : card.pileId;
        let list = PixiSelector.getSelectedIds(card.zone, fetchCards);
        if (! list.includes(id)) {
            list.push(id)
        }
        return list;
    }

    /**
     * Выделенные карты стола
     */
    public static getCardIds(card: PixiCard): Array<number>
    {
        let id = card.cardId;
        let list = PixiSelector.getCardIds();
        if (! list.includes(id)) {
            list.push(id)
        }
        return list;
    }

    /**
     * Перевернуть карты [F]
     */
    public static sendFlip(card: PixiCard, isSimilo = false): void
    {
        if (this.scene.isBanned('canTableFlipCards')) {
            return;
        }
        if (!PixiMessenger.scene || !card
            || (card.locked && !isSimilo)
            || card.isStatic) {
            return;
        }
        const list = PixiMessenger.getSelectedIds(card);
        const uniqueList = Array.from(new Set(list));
        if (card.zone === 'hand') {
            this.scene.handContainer.flipping = true;
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_FLIP_CARDS, {
                ids: uniqueList,
            });
        }
        if (card.zone === 'table') {
            MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_FLIP_STACKS, {
                ids: uniqueList,
            });
        }
        PixiSelector.clearAll();
        EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'flip'});
    }

    /**
     * Заблокировать выбранные элементы [L]
     */
    public static lockTableItems(card: PixiCard): void
    {
        if (this.scene.isBanned('canTableLockCards')) {
            return;
        }
        if (! PixiMessenger.scene || ! card || ! card.parentPile) {
            return;
        }
        if (card.zone === 'table') {
            let type = card.parentPile.locked ? MessageHelper.TYPE_UNLOCK_STACKS : MessageHelper.TYPE_LOCK_STACKS;
            const list = PixiMessenger.getSelectedIds(card);
            const uniqueList = Array.from(new Set(list));
            MessageHelper.sendChangeStacksMessage(type, {
                ids: uniqueList,
            });
            PixiSelector.clearAll();
        }
    }

    /**
     * Перемешать пачку [S]
     */
    public static shuffleSelectedTableItems(card: PixiCard): void
    {
        if (!PixiMessenger.scene || !card || card.locked || card.isStatic || !card.parentPile) {
            return;
        }
        if (card.zone === 'table') {
            let pileList = PixiSelector.getSelected('table');
            if (!pileList.includes(card)) {
                pileList.unshift(card);
            }
            pileList = pileList.filter(pile => pile.pileLength > 1);
            let list = pileList.map(pile => pile.pileId);

            if (list.length) {
                MessageHelper.sendPlaySoundMessage('shuffle');
                MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_SHUFFLE_STACKS, {
                    ids: Array.from(new Set(list)),
                });
            }
            PixiSelector.clearAll();
        }
    }

    /**
     * Переместить карты на стол/руку [T]
     */
    public static moveToHandOrTable(card: PixiCard): void
    {
        if (this.scene.isBanned('canTableTakeCards')) {
            return;
        }
        if (!PixiMessenger.scene || !card || card.locked || card.isStatic) {
            return;
        }
        if (card.zone === 'hand') {
            const list = PixiMessenger.getSelectedIds(card);
            const pos = PixiMessenger.scene.getSceneCenter();
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_CARD_TO_TABLE_FROM_HAND, {
                x: pos.x - card.size.width  / 2,
                y: pos.y - card.size.height / 2,
                ids: Array.from(new Set(list)),
            });
        }
        if (card.zone === 'table') {
            let list = PixiMessenger.getCardIds(card);
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_CARD_TO_PLAYER, {
                ids: Array.from(new Set(list)),
                playerId: PixiMessenger.scene.playerId,
                flip: [],
                handIndex: this.scene.handContainer.handSize,
            });
            EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'itemTakeToHand'});
        }
        PixiSelector.clearAll();
    }

    /**
     * Удалить карты [Del]
     */
    public static deleteCards(card: PixiCard, isAdmin: boolean): void
    {
        if (this.scene.isBanned('canTableDeleteCards')) {
            return;
        }
        if (!PixiMessenger.scene || !card || card.locked || card.isStatic) {
            return;
        }
        const list = PixiMessenger.getSelectedIds(card);
        const uniqueList = Array.from(new Set(list));
        if (card.zone === 'hand') {
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_DELETE_CARDS, {
                ids: uniqueList,
            });
        }
        if (card.zone === 'table') {
            PixiMessenger.scene.pixiMouse.update();
            card.parentPile?.dropRest();
            isAdmin ? MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_HIDE_STACKS, { ids: uniqueList })
                : MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_DELETE_STACKS, { ids: uniqueList });
        }
        PixiSelector.clearAll();
    }

    /**
     * Сортировка карт
     * @param flipList
     */
    public static changeHandItemsSort(flipList: Array<number>): void
    {
        let sortedCardsIds = PixiMessenger.scene.handContainer.getCardIds();
        EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'itemTakeToHand'});
        if (sortedCardsIds.length > 1) {
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_CHANGE_HAND_ITEMS_SORT, {
                ids: sortedCardsIds,
                flip: flipList,
            });
        }
    }

    /**
     * Передать выбранные карты определенному игроку
     * @param card
     * @param playerId
     */
    public static moveToPlayer(card: PixiCard, playerId: string): void
    {
        if (this.scene.isBanned('canTablePassCards')) {
            return;
        }
        if (!PixiMessenger.scene || !card || card.locked || card.isStatic) {
            return;
        }
        let list: Array<number> = [];
        if (card.zone === 'hand') {
            list = PixiMessenger.getSelectedIds(card);
        }
        if (card.zone === 'table') {
            list = PixiMessenger.getCardIds(card);
        }
        const uniqueList = Array.from(new Set(list));
        MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_CARD_TO_PLAYER, {
            ids: uniqueList,
            playerId: playerId,
            flip: [],
            handIndex: this.scene.handContainer.handSize,
        });
    }

    /**
     * Раздать по N карт каждому игроку
     * @param card
     * @param num
     */
    public static dealNCards(card: PixiCard, num: number): void
    {
        if (!PixiMessenger.scene || !card || card.locked || card.isStatic) {
            return;
        }
        if (num > 0) {
            MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_DEAL_STACK, {
                id: card.pileId,
                count: num,
            });
            MessageHelper.sendPlaySoundMessage('dealSeveralCards');
        }
    }

    /**
     * Взять N карт в руку
     * @param card
     * @param num
     */
    public static moveNCardsToPlayer(card: PixiCard, num: number): void
    {
        if (this.scene.isBanned('canTablePassCards')) {
            return;
        }
        if (!PixiMessenger.scene || !card || card.locked || card.isStatic) {
            return;
        }
        if (num > 0 && card.parentPile) {
            const cardsIds = card.parentPile.takeNCards(num);
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_CARD_TO_PLAYER, {
                ids: cardsIds,
                playerId: this.scene.playerId,
                flip: [],
                handIndex: this.scene.handContainer.handSize,
            });
            MessageHelper.sendPlaySoundMessage('dealSeveralCards');
        }
    }

    /**
     * Отправить сообщение о блокировке карты
     * @param ids
     * @param block
     */
    public static sendDisableCardMessage(ids: Array<number>, block: boolean): void {
        if (ids.length == 1 && ids[0] == -1) {
            return;
        }
        MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_DISABLE_CARD_BY_ANOTHER_USAGE, {
            ids: ids, block: block
        });
    }

    /**
     * Отправить сообщение о разблокировании карты
     */
    public static sendEnableCardMessage(ids: Array<number> = []): void {
        if (ids.length == 1 && ids[0] == -1) {
            return;
        }
        MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_ENABLE_CARD_BY_ANOTHER_USAGE, {
            ids: ids
        });
    }

    public static test(text: string): void
    {
        console.log('%c ### PixiMessenger::test', 'color: #f0f', text);
        console.log('%c ### GameScene:', 'color: #f0f', PixiSelector.getTableIds());
        console.log('%c ###  Hand:', 'color: #f0f', PixiSelector.getHandIds());
    }

    /**
     * Повернуть карту
     * @param card
     */
    public static rotateStack(card: PixiCard): void
    {
        let angle = card.angle;
        while (angle < 0) angle += 360;
        angle = Math.round(angle) % 360;
        MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_ROTATE_STACK, {
            id: card.pileId,
            angle: angle,
        });
    }

    /**
     * Повернуть карту на дельту угла
     * @param delta
     * @param card
     */
    public static rotateStackDelta(delta: number, card: PixiCard): void
    {
        MessageHelper.sendChangeStacksMessage(MessageHelper.TYPE_ROTATE_STACK, {
            id: card.pileId,
            ids: PixiMessenger.getSelectedIds(card),
            delta: delta,
        });
    }

    /*/
        Разрешить меню на столе (canViewTableMenu)
        Разрешить горячие клавиши (canViewShortcuts)
        Разрешить блокировать карты (canTableLockCards)
        Разрешить удалять карты (canTableDeleteCards)
        Разрешить передавать карты (canTablePassCards)
        Разрешить брать карты (canTableTakeCards)
        Разрешить переворачивать карты (canTableFlipCards)
        Разрешить увеличивать карты (canTablePreviewCards)
    /*/

}
